import React from 'react';
import { Avatar as MuiAvatar, Box } from '@material-ui/core';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { Avatar, FallbackAvatar, getAvatarColour } from '../../general/user-profile/components/Avatar';
import { AVATAR_SIZE, CommunicationsPerson, personInitials, personName } from './communications-model';

const NAMES_IN_TOOLTIP = 5;

// Everyone is clustered inside one avatar sized circle, the way iMessage shows a group, so a row
// never grows sideways no matter how many recipients there are.
const CLUSTER = [
  [],
  [],
  [
    { size: 0.64, top: 0, right: 0 },
    { size: 0.74, bottom: 0, left: 0 },
  ],
  [
    { size: 0.56, top: 0, left: 0 },
    { size: 0.56, top: 0, right: 0 },
    { size: 0.66, bottom: 0, left: '50%', shiftX: true },
  ],
];

interface RecipientAvatarsProps {
  people: CommunicationsPerson[];
  totalCount: number;
}

const groupTooltip = (people: CommunicationsPerson[], totalCount: number) => {
  const names = people.slice(0, NAMES_IN_TOOLTIP).map(personName);
  const remaining = totalCount - names.length;
  return remaining > 0 ? `${names.join(', ')} and ${remaining} more` : names.join(', ');
};

const MiniAvatar = ({ person, size }: { person: CommunicationsPerson; size: number }) => {
  const initials = personInitials(person);

  return (
    <MuiAvatar
      alt={personName(person)}
      src={person.avatarUrl ?? undefined}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size / 2.2),
        fontWeight: 700,
        color: '#FFFFFF',
        backgroundColor: `#${getAvatarColour(initials)}`,
        border: '2px solid #FFFFFF',
        boxSizing: 'border-box',
      }}
    >
      {initials}
    </MuiAvatar>
  );
};

export const RecipientAvatars = ({ people, totalCount }: RecipientAvatarsProps) => {
  if (people.length === 0) {
    return <FallbackAvatar size={AVATAR_SIZE} initials={null} style={{ flexShrink: 0 }} />;
  }

  if (people.length === 1 && totalCount === 1) {
    const person = people[0];
    return (
      <TitularTooltip description={personName(person)}>
        <Box style={{ flexShrink: 0 }}>
          <Avatar
            size={AVATAR_SIZE}
            preferredName={person.preferredName ?? person.firstName}
            lastName={person.lastName}
            avatarUrl={person.avatarUrl}
          />
        </Box>
      </TitularTooltip>
    );
  }

  const positions = CLUSTER[Math.min(people.length, CLUSTER.length - 1)];
  const shown = people.slice(0, positions.length);

  return (
    <TitularTooltip description={groupTooltip(people, totalCount)}>
      <Box
        style={{
          position: 'relative',
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          flexShrink: 0,
        }}
      >
        {shown.map((person, index) => {
          const { size, shiftX, ...offsets } = positions[index];
          return (
            <Box
              key={person.profileId}
              style={{
                position: 'absolute',
                ...offsets,
                // Later avatars sit in front, so the overlap reads as a stack.
                zIndex: index + 1,
                transform: shiftX ? 'translateX(-50%)' : undefined,
              }}
            >
              <MiniAvatar person={person} size={Math.floor(AVATAR_SIZE * size)} />
            </Box>
          );
        })}
      </Box>
    </TitularTooltip>
  );
};
